import kebabCase from "lodash/kebabCase";
import { DashboardCategory, SearchableDashboard } from "../constants/searchableDashboards";
import { fetchDataV2, patchDataV2, postDataV2 } from "./utils";

// Full resource shape returned by the dedicated category-management endpoints below - richer than
// the {id, name, slug} shape embedded in a GET /dashboards row's own `categories`/`tags` arrays
// (docs/needed-endpoints.md §1 vs §9).
export interface DashboardCategoryDetail extends DashboardCategory {
  description: string | null;
  icon: string | null;
  display_order: number;
  is_active: boolean;
}

// Live (docs/needed-endpoints.md §9/§11) - the real GET-list endpoint that was previously
// unconfirmed, replacing useDashboardCategories.ts's client-side localStorage-merge workaround.
// Lighter {id, name, slug} shape than DashboardCategoryDetail (matches the embedded shape in a
// GET /dashboards row's own `categories` array, not the fuller create/update response below).
export const getDashboardCategoryList = (): Promise<DashboardCategory[]> =>
  fetchDataV2("/dashboards/dashboard_category_list").then((response) => (Array.isArray(response) ? response : []));

// Live. `slug` is required in the request (not server-generated), so it'd still need to be
// derived client-side via kebab-case if this were wired up again. Path moved from
// /dashboards/dashboard-categories to /admin/dashboard-categories (docs/needed-endpoints.md §11).
// No frontend UI calls this anymore - the "Add Category" admin action (and its AddCategoryModal.tsx
// color picker) has been removed from the Dashboards page; left wired matching this codebase's
// usual "wrap it now, build the UI later" pattern in case it's needed again. `color` was never
// part of the confirmed live request shape (docs/needed-endpoints.md §9/§11).
export const createDashboardCategory = (name: string, displayOrder: number, color: string): Promise<DashboardCategoryDetail> =>
  postDataV2("/admin/dashboard-categories", {
    name,
    slug: kebabCase(name),
    description: "",
    icon: "",
    display_order: displayOrder,
    is_active: true,
    color,
  });

// Live (docs/needed-endpoints.md §11) - no frontend UI calls this yet (no "Edit Category" action
// exists on the Dashboards page today), wired ahead of time matching this codebase's usual
// "wrap it now, build the UI later" pattern.
export const updateDashboardCategory = (
  categoryId: number,
  payload: Partial<Pick<DashboardCategoryDetail, "name" | "slug" | "description" | "icon" | "display_order" | "is_active">>
): Promise<DashboardCategoryDetail> => patchDataV2(`/admin/dashboard-categories/${categoryId}`, payload);

// Live - assigns an existing dashboard to an additional category (additive, not a move - the
// dashboard keeps its existing categories too) and returns the dashboard's full updated row. Path
// moved from /dashboards/{dashboard_id}/categories to /admin/dashboards/{dashboard_id}/categories
// (docs/needed-endpoints.md §11). No frontend UI calls this anymore - the "Add Dashboard" admin
// action (AddDashboardModal.tsx) has been removed from the Dashboards page; left wired matching
// this codebase's usual "wrap it now, build the UI later" pattern in case it's needed again.
export const assignDashboardToCategory = (dashboardId: number, categoryId: number): Promise<SearchableDashboard> =>
  postDataV2(`/admin/dashboards/${dashboardId}/categories`, { category_id: categoryId });
